import "./card.css";
import { useState } from "react";
import { Eye } from "lucide-react";
import { routes } from "../../routes";
import { Card } from "react-bootstrap";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { icons } from "../../utils/globalImages.js";
import { useNavigate, useParams } from "react-router-dom";
import BoxModel from "../../components/BoxModel/BoxModel.jsx";
import { truncateText } from "../../utils/HelperFunctions.js";

const CardBox = ({
  item,
  index,
  searchModal,
  setShowOverlay,
  hideSearchModal,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { slugId } = useParams();
  const [showModal, setShowModal] = useState(false);
  const [hoveredCards, setHoveredCards] = useState([]);
  const [showRiskTooltip, setShowRiskTooltip] = useState(false);
  const isDarkMode = useSelector((state) => state?.theme?.darkMode);

  const handleEyeClick = (e) => {
    e.stopPropagation();
    setShowModal(true);
  };

  const handleOverlayClick = () => {
    setShowOverlay((prevState) => !prevState);
  };

  const encryptData = (data) => {
    const base64EncodedData = window?.btoa(data?.toString());
    return base64EncodedData;
  };

  const handleClick = () => {
    const boxNameEncrypt = encryptData(item?.slug);
    navigate(`${routes.BOXDETAIL}/${boxNameEncrypt}`);
    handleOverlayClick();
    if (searchModal) hideSearchModal();
  };

  const handleMouseIN = (cardIndex) => {
    setHoveredCards((prevHoveredCards) => [...prevHoveredCards, cardIndex]);
  };

  const handleMouseOUT = (cardIndex) => {
    setHoveredCards((prevHoveredCards) =>
      prevHoveredCards.filter((index) => index !== cardIndex),
    );
  };

  const getRiskLevelValue = (riskLevel) => {
    switch (riskLevel.toLowerCase()) {
      case "easy":
        return 14;
      case "normal":
        return 32;
      case "mild":
        return 50;
      case "hard":
        return 68;
      case "extremely hard":
        return 86;
      default:
        return 10;
    }
  };

  const getRiskColor = (riskLevel) => {
    switch (riskLevel?.toLowerCase()) {
      case "easy":
        return "#4ca52e";
      case "normal":
        return "#65d83e";
      case "mild":
        return "#ffc93f";
      case "hard":
        return "#f99100";
      case "extremely hard":
        return "#cb0003";
      default:
        return "#4ca52e";
    }
  };

  const getRiskSegmentIndex = (riskLevel) => {
    switch (riskLevel?.toLowerCase()) {
      case "easy":
        return 0;
      case "normal":
        return 1;
      case "mild":
        return 2;
      case "hard":
        return 3;
      case "extremely hard":
        return 4;
      default:
        return 0;
    }
  };

  const getRiskTooltipText = (riskLevel) => {
    switch (riskLevel?.toLowerCase()) {
      case "easy":
        return "Very Low";
      case "normal":
        return "Low";
      case "mild":
        return "Medium";
      case "hard":
        return "High";
      case "extremely hard":
        return "Extreme";
      default:
        return "";
    }
  };

  return (
    item && (
      <Card
        className={`custom-card-home ${
          isDarkMode
            ? "custom-card-home-dark dark"
            : "custom-card-home-light light"
        }`}
        onMouseEnter={() => handleMouseIN(index)}
        onMouseLeave={() => handleMouseOUT(index)}
        onClick={handleClick}
      >
        {/* Product Image */}
        <div className="card-img-wrapper">
          <div className="position-absolute badges">
            <Eye
              size={20}
              onClick={handleEyeClick}
              style={{ cursor: "pointer", opacity: "0.2" }}
              color={isDarkMode ? "#E8DEF8" : "#684199"}
            />
          </div>
          <Card.Img
            variant="top"
            className="card-img cardimg"
            src={item?.box_images ? item?.box_images?.image : item?.image}
          />
        </div>

        {/* Card Body */}
        <Card.Body className="card-body">
          <Card.Title
            className={`card-title ${isDarkMode ? "text-light-gray" : "text-light-gray"} `}
          >
            {item?.name ? truncateText(item.name, 25) : "Unnamed Box"}
          </Card.Title>
          <p
            className="card-price m-0"
            style={{
              color: isDarkMode ? "#fff" : "#fff",
            }}
          >
            {item?.formatted_box_price
              ? item.formatted_box_price
              : item.formatted_price}
          </p>
        </Card.Body>
        <div className="risk-bar-container pb-4 d-center">
          <div className="risk-bar">
            {["green", "light-green", "yellow", "orange", "red"].map(
              (color, idx) => (
                <div
                  key={idx}
                  className={`risk-segment ${color} ${
                    idx === getRiskSegmentIndex(item?.risk_level)
                      ? "active-risk-segment"
                      : ""
                  }`}
                  onMouseEnter={() => {
                    if (idx === getRiskSegmentIndex(item?.risk_level)) {
                      setShowRiskTooltip(true);
                    }
                  }}
                  onMouseLeave={() => setShowRiskTooltip(false)}
                />
              ),
            )}
          </div>
          <div
            className="risk-indicator tooltip-container"
            style={{
              left: `${getRiskLevelValue(item?.risk_level)}%`,
              "--tooltip-bg": getRiskColor(item?.risk_level),
            }}
            onMouseEnter={() => setShowRiskTooltip(true)}
            onMouseLeave={() => setShowRiskTooltip(false)}
          >
            <img
              src={
                isDarkMode ? icons.riskIndicatorDark : icons.riskIndicatorLight
              }
              alt="indicator"
            />
            <span
              className={`tooltip-text ${
                showRiskTooltip ? "tooltip-visible" : ""
              }`}
            >
              {getRiskTooltipText(item?.risk_level)}
            </span>
          </div>
        </div>
        <BoxModel
          show={showModal}
          onHide={() => setShowModal(false)}
          item={item}
          isDarkMode={isDarkMode}
          onOpenBox={handleClick}
        />
      </Card>
    )
  );
};

export default CardBox;
